import { useEffect, useState } from 'react';
import type { PlannerRegion } from '../../types';
import type { RecommendationAccessType, RecommendationBudgetBand } from '../recommendations/catalog';
import { DEFAULT_PLANNER_REGION, getCurrentPlannerMonth } from './seasonality';

const STORAGE_KEYS = {
  monthIndex: 'planner.monthIndex',
  monthStamp: 'planner.monthStamp',
  region: 'planner.region',
  accessType: 'planner.accessType',
  budgetBand: 'planner.budgetBand'
} as const;

export const PLANNER_PREFERENCE_STORAGE_KEYS = STORAGE_KEYS;

export interface PlannerPreferences {
  monthIndex: number;
  region: PlannerRegion;
  accessType: RecommendationAccessType;
  budgetBand: RecommendationBudgetBand;
}

const clampMonthIndex = (value: unknown): number => {
  const parsed = Number(value);
  if (Number.isInteger(parsed) && parsed >= 0 && parsed <= 11) {
    return parsed;
  }

  return getCurrentPlannerMonth();
};

const getPlannerMonthStamp = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const normalizeRegion = (value: unknown): PlannerRegion => {
  const candidate = typeof value === 'string' ? value : '';
  if (candidate === 'tyrrhenian' || candidate === 'adriatic' || candidate === 'ionian' || candidate === 'sicily_channel' || candidate === 'western_med' || candidate === 'aegean_cyprus') {
    return candidate;
  }

  return DEFAULT_PLANNER_REGION;
};

const normalizeAccessType = (value: unknown): RecommendationAccessType => {
  if (value === 'boat' || value === 'both' || value === 'shore') {
    return value;
  }

  return 'shore';
};

const normalizeBudgetBand = (value: unknown): RecommendationBudgetBand => {
  if (value === 'mid' || value === 'premium' || value === 'budget') {
    return value;
  }

  return 'budget';
};

export const getStoredPlannerPreferences = (): PlannerPreferences => {
  if (typeof window === 'undefined') {
    return {
      monthIndex: getCurrentPlannerMonth(),
      region: DEFAULT_PLANNER_REGION,
      accessType: 'shore',
      budgetBand: 'budget'
    };
  }

  const currentMonth = getCurrentPlannerMonth();
  const storedMonthStamp = window.localStorage.getItem(STORAGE_KEYS.monthStamp);
  const hasFreshMonth = storedMonthStamp === getPlannerMonthStamp();

  return {
    monthIndex: hasFreshMonth ? clampMonthIndex(window.localStorage.getItem(STORAGE_KEYS.monthIndex)) : currentMonth,
    region: normalizeRegion(window.localStorage.getItem(STORAGE_KEYS.region)),
    accessType: normalizeAccessType(window.localStorage.getItem(STORAGE_KEYS.accessType)),
    budgetBand: normalizeBudgetBand(window.localStorage.getItem(STORAGE_KEYS.budgetBand))
  };
};

export const persistPlannerPreferences = (preferences: PlannerPreferences): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEYS.monthIndex, String(clampMonthIndex(preferences.monthIndex)));
  window.localStorage.setItem(STORAGE_KEYS.monthStamp, getPlannerMonthStamp());
  window.localStorage.setItem(STORAGE_KEYS.region, normalizeRegion(preferences.region));
  window.localStorage.setItem(STORAGE_KEYS.accessType, normalizeAccessType(preferences.accessType));
  window.localStorage.setItem(STORAGE_KEYS.budgetBand, normalizeBudgetBand(preferences.budgetBand));
};

export const usePlannerPreferences = () => {
  const [preferences, setPreferences] = useState<PlannerPreferences>(() => getStoredPlannerPreferences());

  useEffect(() => {
    persistPlannerPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const syncPreferences = (event: StorageEvent) => {
      if (
        event.key &&
        event.key !== STORAGE_KEYS.monthIndex &&
        event.key !== STORAGE_KEYS.region &&
        event.key !== STORAGE_KEYS.accessType &&
        event.key !== STORAGE_KEYS.budgetBand
      ) {
        return;
      }

      setPreferences(getStoredPlannerPreferences());
    };

    window.addEventListener('storage', syncPreferences);
    return () => window.removeEventListener('storage', syncPreferences);
  }, []);

  return {
    monthIndex: preferences.monthIndex,
    region: preferences.region,
    accessType: preferences.accessType,
    budgetBand: preferences.budgetBand,
    setMonthIndex: (monthIndex: number) => setPreferences((current) => ({ ...current, monthIndex: clampMonthIndex(monthIndex) })),
    setRegion: (region: PlannerRegion) => setPreferences((current) => ({ ...current, region: normalizeRegion(region) })),
    setAccessType: (accessType: RecommendationAccessType) => setPreferences((current) => ({ ...current, accessType: normalizeAccessType(accessType) })),
    setBudgetBand: (budgetBand: RecommendationBudgetBand) => setPreferences((current) => ({ ...current, budgetBand: normalizeBudgetBand(budgetBand) })),
    setPreferences
  };
};
